import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Globe, Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import ProPortalHeader from '@/components/ProPortalHeader';
import { useConfirm } from '@/components/ConfirmDialog';
import { STATUS_FLOW, STATUS_TONE, wdel, wget } from '@/components/website/api';
import { STEPS } from '@/pages/WebsiteWizard';
import { WebsiteAdvert } from '@/components/website/WebsiteAdvert';

const LABELS = { submitted: 'Submitted', info_required: 'Information required', in_development: 'In development', ready_for_review: 'Ready for review', changes_requested: 'Changes requested', approved: 'Approved', live: 'Live' };
const TRACK = ['submitted', 'in_development', 'ready_for_review', 'approved', 'live'];

export function StatusTracker({ status }) {
  const idx = Math.max(0, TRACK.indexOf(status === 'info_required' ? 'submitted' : status === 'changes_requested' ? 'ready_for_review' : status));
  return (
    <ol className="flex items-center gap-1" data-testid="ws-tracker">
      {TRACK.map((s, i) => (
        <li key={s} className="flex-1 flex flex-col items-center gap-1 text-center">
          <span className={`w-full h-1.5 rounded-full ${i <= idx ? 'bg-teal-600' : 'bg-slate-200'}`} />
          <span className={`text-[10px] leading-tight ${i <= idx ? 'text-teal-800 font-semibold' : 'text-slate-400'}`}>{LABELS[s]}</span>
        </li>
      ))}
    </ol>
  );
}

export default function MyWebsite() {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [data, setData] = useState(null);
  const load = () => wget('/website/me').then(setData).catch((e) => toast.error(e.message));
  useEffect(() => { load(); }, []);

  const discard = async () => {
    if (!(await confirm({ title: 'Delete this draft?', body: 'All the answers and photos you have added so far will be removed.', confirmLabel: 'Delete draft', tone: 'danger' }))) return;
    try { await wdel('/website/draft'); toast.success('Draft deleted'); load(); } catch (e) { toast.error(e.message); }
  };

  const draft = data?.draft;
  const orders = data?.orders || [];
  return (
    <div className="min-h-screen bg-[#E8ECEF]" data-testid="my-website-page">
      <ProPortalHeader />
      <main className="max-w-3xl mx-auto px-4 py-6 md:py-10">
        <button onClick={() => navigate('/')} className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 mb-3" data-testid="mw-back"><ArrowLeft className="w-4 h-4" /> Home</button>
        <h1 className="text-3xl font-bold text-[#27292E] flex items-center gap-2"><Globe className="w-7 h-7 text-teal-700" /> My Website</h1>
        <p className="text-slate-600 mt-1 mb-6">A professional website for your business — you supply the details, Ox Resin builds it, sorts your domain and email, and keeps it running.</p>
        {!data && <Loader2 className="w-6 h-6 animate-spin text-slate-400" />}

        {orders.map((o) => (
          <button type="button" key={o.id} onClick={() => navigate(`/my-website/${o.id}`)} className="w-full text-left bg-white rounded-2xl border border-slate-200 shadow-sm p-5 mb-4 hover:border-teal-500 transition-colors relative" data-testid={`mw-order-${o.id}`}>
            {o.unread > 0 && <span className="absolute -top-2 -left-2 inline-flex items-center justify-center h-6 min-w-[24px] px-1.5 rounded-full bg-red-600 text-white text-xs font-bold ring-2 ring-white" data-testid={`mw-order-unread-${o.id}`}>{o.unread}</span>}
            <div className="flex items-start justify-between gap-3">
              <div><div className="text-xs text-slate-500">{o.reference}</div><div className="font-bold text-slate-900 text-lg">{o.form?.business?.business_name || 'Your website'}</div></div>
              <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${STATUS_TONE[o.status]}`} data-testid={`mw-order-status-${o.id}`}>{o.status_label}</span>
            </div>
            <div className="mt-4"><StatusTracker status={o.status} /></div>
            <p className="text-xs text-slate-500 mt-3 inline-flex items-center gap-1">{o.status === 'ready_for_review' ? 'Your preview is ready — tap to review and approve' : o.status === 'info_required' ? 'We need some information from you — tap to reply' : o.status === 'live' ? 'Tap to see your live site, domain and email addresses' : `Submitted ${o.submitted_at ? new Date(o.submitted_at).toLocaleDateString('en-GB') : ''} — tap for messages and progress`} <ArrowRight className="w-3 h-3" /></p>
          </button>
        ))}

        {draft && (
          <div className="bg-white rounded-2xl border-2 border-dashed border-teal-400 p-5 mb-4" data-testid="mw-draft">
            <div className="text-xs font-bold uppercase tracking-wider text-teal-700">{draft.payment?.offline ? 'Paid — just add your details' : 'Order in progress'}</div>
            <div className="font-bold text-slate-900 text-lg mt-1">{draft.form?.business?.business_name || 'Your new website'}</div>
            {draft.payment?.offline && <p className="text-sm text-emerald-700 font-semibold mt-1" data-testid="mw-paid-offline">Your website package is paid ({draft.payment.method_label}). Tell us about your business so we can start building.</p>}
            <p className="text-sm text-slate-600 mt-1">You're on step {(draft.current_step || 0) + 1} of {STEPS.length} — {STEPS[Math.min(draft.current_step || 0, STEPS.length - 1)].label}. Pick up where you left off.</p>
            <div className="flex gap-2 mt-4">
              <Button onClick={() => navigate('/my-website/order')} className="flex-1 h-11 bg-teal-700 text-white" data-testid="mw-continue">{draft.payment?.offline ? 'Fill in my details' : 'Continue order'} <ArrowRight className="w-4 h-4 ml-1" /></Button>
              {!draft.payment?.offline && <Button variant="outline" onClick={discard} className="h-11 text-rose-600" data-testid="mw-discard"><Trash2 className="w-4 h-4" /></Button>}
            </div>
          </div>
        )}

        {data && !draft && (
          <WebsiteAdvert onStart={() => navigate('/my-website/order')} hasOrders={!!orders.length} ctaNote={data.included ? 'Included in your package — nothing more to pay' : 'Web + CRM package · £200 setup · £50 a month'} />
        )}
      </main>
    </div>
  );
}
